import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import { fmtDateTime, fmtTime } from '../../../utils/ruDates';

const STATUS_TABS = [
  { key: '', label: 'Все' },
  { key: 'running', label: 'Выполняется' },
  { key: 'queued', label: 'В очереди' },
  { key: 'success', label: 'Успешно' },
  { key: 'done', label: 'Готово' },
  { key: 'failed', label: 'Ошибка' },
];

const STATUS_COLORS = {
  success: { color: '#27ae60', bg: '#eafaf1' },
  done:    { color: '#27ae60', bg: '#eafaf1' },
  failed:  { color: '#e74c3c', bg: '#fdedec' },
  running: { color: '#2980b9', bg: '#ebf5fb' },
  queued:  { color: '#7f8c8d', bg: '#f4f6f7' },
};

const JOB_TYPE_LABELS = {
  ozon_sync_full:            'Синхронизация OZON (полная)',
  ozon_sync_fast:            'Синхронизация OZON (быстрая)',
  api_tools_inventory_sync:  'Инвентаризация API-инструментов',
  ozon_fbo_daily_inventory:  'OZON FBO снимок остатков',
  daily_stats_sync:          'Статистика маркетплейсов',
  ozon_cluster_sync:         'Кластеры OZON',
  billing_charge:            'Списание',
  gdpr_purge:                'GDPR очистка',
  ai_agent_task:             'AI-задача',
};

const RESULT_LABELS = {
  attributes_count: 'Атрибутов',
  assortment_count: 'Товаров',
  synced:           'Синхронизировано',
  total:            'Всего',
  processed:        'Обработано',
  skipped:          'Пропущено',
  rows_total:       'Строк',
  failed:           'Ошибок',
  created:          'Создано',
  updated:          'Обновлено',
  deleted:          'Удалено',
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || { color: '#555', bg: '#eee' };
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 4, fontSize: 12,
      fontWeight: 700, textTransform: 'uppercase',
      color: s.color, background: s.bg,
    }}>
      {status}
    </span>
  );
}

function ResultSummary({ result, error }) {
  if (error) {
    return <span style={{ color: '#e74c3c', fontSize: 12 }} title={error}>❌ {error.slice(0, 90)}</span>;
  }
  if (!result || typeof result !== 'object') return <span style={{ color: '#ccc' }}>—</span>;

  const entries = Object.entries(result)
    .filter(([k, v]) => k !== 'status' && k !== 'error' && typeof v === 'number');
  if (entries.length === 0) return <span style={{ color: '#27ae60', fontSize: 12 }}>✅ Выполнено</span>;

  return (
    <div style={{ fontSize: 12, color: '#555' }}>
      {entries.map(([k, v]) => (
        <span key={k} style={{ marginRight: 10 }}>
          <span style={{ color: '#999' }}>{RESULT_LABELS[k] || k}:</span>{' '}
          <strong>{v}</strong>
        </span>
      ))}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  return fmtDateTime(iso);
}

function fmtShort(iso) {
  if (!iso) return '—';
  return fmtTime(iso);
}

function duration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (ms < 0) return null;
  if (ms < 60000) return `${Math.round(ms / 1000)}с`;
  return `${Math.round(ms / 60000)}мин`;
}

// ---------- Job log ----------

function JobsTable({ jobs, loading }) {
  if (loading && jobs.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Загрузка...</div>;
  }
  if (!loading && jobs.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#bbb' }}>Задач нет</div>;
  }

  const th = { padding: '11px 13px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#666', whiteSpace: 'nowrap' };
  const td = { padding: '10px 13px', verticalAlign: 'top' };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
          <th style={th}>Тип задачи</th>
          <th style={th}>Проект</th>
          <th style={th}>Кабинет</th>
          <th style={th}>Статус</th>
          <th style={th}>Начало</th>
          <th style={th}>Завершена</th>
          <th style={th}>Длит.</th>
          <th style={th}>Результат / Ошибка</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map(j => (
          <tr key={j.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={td}>
              <div style={{ fontWeight: 500 }}>{JOB_TYPE_LABELS[j.job_type] || j.job_type}</div>
              {j.job_type === 'ai_agent_task' && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 3 }}>
                  {j.payload?.title || 'Задача'}{j.payload?.ai_agent_slug ? ` · ${j.payload.ai_agent_slug}` : ''}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#bbb', marginTop: 2 }}>{j.id.slice(0, 8)} · {j.trigger_type} · {j.mode}</div>
            </td>
            <td style={{ ...td, color: '#555' }}>{j.project_name || <span style={{ color: '#ddd' }}>—</span>}</td>
            <td style={{ ...td, color: '#555' }}>{j.cabinet_name || <span style={{ color: '#ddd' }}>—</span>}</td>
            <td style={td}><StatusBadge status={j.status} /></td>
            <td style={{ ...td, color: '#666', fontSize: 12 }}>{fmt(j.created_at)}</td>
            <td style={{ ...td, color: '#666', fontSize: 12 }}>{fmtShort(j.finished_at)}</td>
            <td style={{ ...td, color: '#aaa', fontSize: 12 }}>{duration(j.created_at, j.finished_at) || '—'}</td>
            <td style={td}><ResultSummary result={j.result} error={j.error} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------- Scheduled job editor ----------

function ScheduledJobEditor({ job, onSave, onCancel, isSystemLoop }) {
  const [form, setForm] = useState({
    job_key:          job.job_key,
    title:            job.title,
    job_type:         job.job_type,
    mode_default:     job.mode_default,
    enabled:          job.enabled,
    schedule_type:    job.schedule_type,
    schedule_hour:    job.schedule_hour,
    schedule_minute:  job.schedule_minute,
    payload_template: job.payload_template ? JSON.stringify(job.payload_template, null, 2) : '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    let payload_template = null;
    if (form.payload_template.trim()) {
      try { payload_template = JSON.parse(form.payload_template); }
      catch { alert('Невалидный JSON в payload_template'); return; }
    }
    onSave({ ...form, schedule_hour: +form.schedule_hour, schedule_minute: +form.schedule_minute, payload_template });
  };

  const inp = { border: '1px solid #ddd', borderRadius: 4, padding: '6px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ background: '#f9fafc', border: '1px solid #e0e4ea', borderRadius: 8, padding: 20, marginTop: 8 }}>
      {isSystemLoop && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 5, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#795548' }}>
          ⚠️ Системный планировщик: изменения сохранятся в БД, но для применения расписания требуется перезапуск сервиса.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
          Название
          <input style={inp} value={form.title} onChange={e => set('title', e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
          Режим
          <select style={inp} value={form.mode_default} onChange={e => set('mode_default', e.target.value)}>
            <option value="apply">apply</option>
            <option value="dry_run">dry_run</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
          Час запуска (UTC)
          <input style={inp} type="number" min={0} max={23} value={form.schedule_hour} onChange={e => set('schedule_hour', e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
          Минута запуска
          <input style={inp} type="number" min={0} max={59} value={form.schedule_minute} onChange={e => set('schedule_minute', e.target.value)} />
        </label>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
        Включено
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666', marginBottom: 14 }}>
        payload_template (JSON)
        <textarea style={{ ...inp, fontFamily: 'monospace', height: 72, resize: 'vertical' }}
          value={form.payload_template} onChange={e => set('payload_template', e.target.value)} />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSave}
          style={{ padding: '7px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
          Сохранить
        </button>
        <button onClick={onCancel}
          style={{ padding: '7px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
          Отмена
        </button>
      </div>
    </div>
  );
}

// ---------- Schedulers tab ----------

function SchedulersSection({ title, jobs, isSystemLoop, request, onReload, running, setRunning }) {
  const [editing, setEditing] = useState(null);

  const handleSave = async (job_key, data) => {
    try {
      await request('/api/v1/admin/schedules', { method: 'POST', body: JSON.stringify(data) });
      setEditing(null);
      onReload();
    } catch (e) {
      logger.error('Save failed', e);
      alert('Ошибка сохранения');
    }
  };

  const handleRunNow = async (id) => {
    setRunning(id);
    try {
      await request(`/api/v1/admin/schedules/${id}/run`, { method: 'POST' });
      setTimeout(onReload, 2000);
    } catch (e) { alert('Ошибка запуска'); }
    finally { setRunning(null); }
  };

  const th = { padding: '10px 13px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#666' };
  const td = { padding: '10px 13px', fontSize: 13 };

  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#444', fontWeight: 600 }}>{title}</h3>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
            <tr>
              <th style={th}>Задача</th>
              <th style={th}>Расписание (UTC)</th>
              <th style={th}>Режим</th>
              <th style={th}>Вкл</th>
              <th style={th}>Последний запуск</th>
              <th style={th}>Следующий</th>
              <th style={th}>Статус</th>
              <th style={{ ...th, width: 180 }}></th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, color: '#bbb', textAlign: 'center', padding: 24 }}>Нет задач</td></tr>
            )}
            {jobs.map(s => (
              <React.Fragment key={s.id}>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 500 }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: '#bbb', marginTop: 1, fontFamily: 'monospace' }}>{s.job_key}</div>
                  </td>
                  <td style={{ ...td, color: '#555' }}>
                    {s.schedule_type} {String(s.schedule_hour).padStart(2, '0')}:{String(s.schedule_minute).padStart(2, '0')}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: '#888' }}>{s.mode_default}</td>
                  <td style={td}>
                    <span style={{ color: s.enabled ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                      {s.enabled ? '✓' : '✗'}
                    </span>
                  </td>
                  <td style={{ ...td, color: '#666', fontSize: 12 }}>{fmt(s.last_run_at)}</td>
                  <td style={{ ...td, color: '#666', fontSize: 12 }}>{fmt(s.next_run_at)}</td>
                  <td style={td}><StatusBadge status={s.last_status || 'queued'} /></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditing(editing === s.id ? null : s.id)}
                      style={{ marginRight: 6, padding: '4px 10px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 12 }}>
                      ✏️ Редакт.
                    </button>
                    {!isSystemLoop && (
                      <button onClick={() => handleRunNow(s.id)} disabled={running === s.id}
                        style={{ padding: '4px 10px', border: '1px solid #3b82f6', borderRadius: 4, background: '#eff6ff', color: '#3b82f6', cursor: 'pointer', fontSize: 12 }}>
                        {running === s.id ? '...' : '▶'}
                      </button>
                    )}
                  </td>
                </tr>
                {editing === s.id && (
                  <tr key={s.id + '_edit'}>
                    <td colSpan={8} style={{ padding: '0 13px 13px' }}>
                      <ScheduledJobEditor
                        job={s}
                        isSystemLoop={isSystemLoop}
                        onSave={data => handleSave(s.job_key, data)}
                        onCancel={() => setEditing(null)}
                      />
                    </td>
                  </tr>
                )}
                {s.last_error && (
                  <tr key={s.id + '_err'} style={{ background: '#fff5f5' }}>
                    <td colSpan={8} style={{ padding: '5px 13px', fontSize: 12, color: '#e74c3c' }}>
                      ❌ {s.last_error}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FRONTEND_PROCESSES = [
  {
    key: 'admin_counts_polling',
    title: 'Опрос счётчиков меню',
    interval: 'каждые 60 сек',
    description: 'Считает новые тикеты поддержки, запросы агентств, пользователей, транзакции и трансферы — для значков в меню админки.',
    endpoint: 'GET /api/v1/admin/counts',
    source: 'AdminLayout.js → setInterval(fetchCounts, 60_000)',
  },
];

function FrontendProcessesTable() {
  const th = { padding: '10px 13px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#666' };
  const td = { padding: '10px 13px', fontSize: 13 };
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#444', fontWeight: 600 }}>
        Frontend-процессы (polling)
      </h3>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
            <tr>
              <th style={th}>Процесс</th>
              <th style={th}>Эндпоинт</th>
              <th style={th}>Интервал</th>
              <th style={th}>Описание</th>
              <th style={th}>Источник</th>
            </tr>
          </thead>
          <tbody>
            {FRONTEND_PROCESSES.map(p => (
              <tr key={p.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ ...td, fontWeight: 500 }}>{p.title}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: '#555' }}>{p.endpoint}</td>
                <td style={{ ...td, color: '#3b82f6', fontWeight: 600, fontSize: 12 }}>{p.interval}</td>
                <td style={{ ...td, color: '#666', fontSize: 12 }}>{p.description}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: '#aaa' }}>{p.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScheduledJobsPanel({ request }) {
  const [dbJobs, setDbJobs] = useState([]);
  const [loopJobs, setLoopJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sch, loops] = await Promise.all([
        request('/api/v1/admin/schedules'),
        request('/api/v1/admin/jobs/system-loops'),
      ]);
      const allKeys = (loops || []).map(l => l.job_key);
      const dbOnly = ((sch?.items) || []).filter(j => !allKeys.includes(j.job_key));
      setDbJobs(dbOnly);
      setLoopJobs(loops || []);
    } catch (e) { logger.error('Failed to load schedules', e); }
    finally { setLoading(false); }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 30, color: '#999' }}>Загрузка...</div>;

  return (
    <div>
      <SchedulersSection
        title="DB-задачи (запускаются планировщиком)"
        jobs={dbJobs}
        isSystemLoop={false}
        request={request}
        onReload={load}
        running={running}
        setRunning={setRunning}
      />
      <SchedulersSection
        title="Системные планировщики (asyncio loops)"
        jobs={loopJobs}
        isSystemLoop={true}
        request={request}
        onReload={load}
        running={running}
        setRunning={setRunning}
      />
      <FrontendProcessesTable />
    </div>
  );
}

// ---------- Main ----------

const PAGE_SIZE = 50;

function AdminJobs() {
  const { request } = useApi();
  const [tab, setTab] = useState('log');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadJobs = useCallback(async (newOffset = 0, newStatus = statusFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE + 1, offset: newOffset });
      if (newStatus) params.set('status', newStatus);
      const data = await request(`/api/v1/admin/jobs?${params}`);
      const arr = data || [];
      const page = arr.slice(0, PAGE_SIZE);
      if (newOffset === 0) setJobs(page);
      else setJobs(prev => [...prev, ...page]);
      setHasMore(arr.length > PAGE_SIZE);
      setOffset(newOffset);
    } catch (e) { logger.error('Failed to load jobs', e); }
    finally { setLoading(false); }
  }, [request, statusFilter]);  // eslint-disable-line

  useEffect(() => {
    if (tab === 'log') loadJobs(0, statusFilter);
  }, [tab, statusFilter]);  // eslint-disable-line

  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{
      padding: '8px 20px', cursor: 'pointer', border: 'none',
      borderBottom: tab === key ? '2px solid #3b82f6' : '2px solid transparent',
      background: 'none', fontWeight: tab === key ? 700 : 400,
      color: tab === key ? '#3b82f6' : '#555', fontSize: 14,
    }}>{label}</button>
  );

  const statusBtn = (s, label) => (
    <button key={s} onClick={() => { setStatusFilter(s); setOffset(0); }} style={{
      padding: '5px 14px', cursor: 'pointer', borderRadius: 20,
      border: statusFilter === s ? '1px solid #3b82f6' : '1px solid #ddd',
      background: statusFilter === s ? '#eff6ff' : '#fff',
      color: statusFilter === s ? '#3b82f6' : '#666',
      fontSize: 12, fontWeight: statusFilter === s ? 600 : 400,
    }}>{label}</button>
  );

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0 }}>Фоновые задачи</h1>
          <p style={{ color: '#888', margin: '4px 0 0', fontSize: 14 }}>
            Мониторинг очередей, планировщиков и архив выполнений
          </p>
        </div>
        {tab === 'log' && (
          <button onClick={() => loadJobs(0)} style={{
            padding: '8px 16px', background: '#fff', border: '1px solid #ddd',
            borderRadius: 6, cursor: 'pointer', fontSize: 13,
          }}>↻ Обновить</button>
        )}
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        {tabBtn('log', 'Лог задач')}
        {tabBtn('schedulers', 'Планировщики')}
      </div>

      {tab === 'log' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {STATUS_TABS.map(st => statusBtn(st.key, st.label))}
          </div>
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <JobsTable jobs={jobs} loading={loading} />
          </div>
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button onClick={() => loadJobs(offset + PAGE_SIZE)} style={{
                padding: '8px 24px', border: '1px solid #ddd',
                borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13,
              }}>Загрузить ещё</button>
            </div>
          )}
        </div>
      )}

      {tab === 'schedulers' && <ScheduledJobsPanel request={request} />}
    </div>
  );
}

export default AdminJobs;
